import { useRef, useState, type DragEvent } from "react";
import { Box, Button, Callout, Card, Flex, Progress, Text } from "@radix-ui/themes";
import { AlertCircle, Upload } from "lucide-react";
import { mapWithConcurrency, uploadFile } from "./directUpload";
import { humanSize } from "./format";

export interface UploadedFile {
  signedId: string;
  file: File;
}

interface Props {
  /** Mints presigned PUTs; see ClubManager::GalleryDirectUploadsController. */
  directUploadPath: string;
  /** `accept` for the file input, and what the drop target advertises. */
  accept: string;
  title: string;
  hint: string;
  /** What the club has left. Used to turn a batch away before any of it moves —
   *  the server enforces the same limit at admission, this just means nobody
   *  waits out a 4GB upload to be told it didn't fit. */
  remainingBytes: number;
  /** Called once per batch with everything that made it into the bucket. */
  onUploaded: (uploaded: UploadedFile[]) => void;
}

type Status = "pending" | "uploading" | "done" | "failed";

interface Item {
  name: string;
  percent: number;
  status: Status;
}

// In flight at once. Enough to keep the connection busy, few enough not to open
// a token request per file simultaneously.
const CONCURRENCY = 4;

// How much of the queue to actually list. Two hundred rows of "waiting" is not
// information; a count is.
const VISIBLE_QUEUE = 20;

export default function UploadDropzone({
  directUploadPath,
  accept,
  title,
  hint,
  remainingBytes,
  onUploaded,
}: Props) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [items, setItems] = useState<Item[]>([]);
  const [busy, setBusy] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [refused, setRefused] = useState<string | null>(null);

  const full = remainingBytes <= 0;
  const locked = busy || full;

  const patch = (index: number, fields: Partial<Item>) =>
    setItems((current) =>
      current.map((item, i) => (i === index ? { ...item, ...fields } : item)),
    );

  const handleFiles = async (fileList: FileList | File[]) => {
    const files = Array.from(fileList);
    if (files.length === 0 || locked) return;

    setRefused(null);

    // Checked here as a courtesy, not as the control. The server settles the
    // same question at admission — this only spares somebody a long upload that
    // was never going to be accepted.
    const total = files.reduce((sum, file) => sum + file.size, 0);
    if (total > remainingBytes) {
      setRefused(
        `Those ${files.length} ${files.length === 1 ? "file" : "files"} come to ` +
          `${humanSize(total)}, but only ${humanSize(remainingBytes)} is free. ` +
          `Nothing was uploaded.`,
      );
      if (inputRef.current) inputRef.current.value = "";
      return;
    }

    setBusy(true);
    setItems(files.map((f) => ({ name: f.name, percent: 0, status: "pending" })));

    const results = await mapWithConcurrency(files, CONCURRENCY, async (file, index) => {
      patch(index, { status: "uploading" });
      try {
        const signedId = await uploadFile(file, directUploadPath, (percent) =>
          patch(index, { percent }),
        );
        patch(index, { status: "done", percent: 100 });
        return { signedId, file };
      } catch {
        // One bad file shouldn't discard the batch — mark it and let everything
        // that did land be admitted.
        patch(index, { status: "failed" });
        return null;
      }
    });

    const uploaded = results.filter((r): r is UploadedFile => r !== null);
    if (uploaded.length > 0) onUploaded(uploaded);

    // Successes are about to show up in the list below, so they stop earning
    // space here. Failures stay put — nobody learns about them otherwise.
    setItems((current) => current.filter((item) => item.status === "failed"));
    setBusy(false);
    if (inputRef.current) inputRef.current.value = "";
  };

  const onDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    if (!locked) setDragging(true);
  };

  const onDragLeave = (e: DragEvent<HTMLDivElement>) => {
    // Moving onto a child fires dragleave on the parent, which would flicker the
    // highlight off. Only a departure that actually leaves the box counts.
    if (e.currentTarget.contains(e.relatedTarget as Node | null)) return;
    setDragging(false);
  };

  const onDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragging(false);
    if (e.dataTransfer.files.length > 0) void handleFiles(e.dataTransfer.files);
  };

  const done = items.filter((i) => i.status === "done").length;
  const failed = items.filter((i) => i.status === "failed");
  const outstanding = items.filter(
    (i) => i.status === "uploading" || i.status === "pending",
  );
  const listed = outstanding.slice(0, VISIBLE_QUEUE);
  const waiting = outstanding.length - listed.length;

  return (
    <Card>
      <Flex direction="column" gap="3">
        <Box
          onDragOver={onDragOver}
          onDragLeave={onDragLeave}
          onDrop={onDrop}
          onClick={() => !locked && inputRef.current?.click()}
          style={{
            border: `2px dashed ${dragging ? "var(--accent-9)" : "var(--gray-6)"}`,
            backgroundColor: dragging ? "var(--accent-2)" : "transparent",
            borderRadius: "var(--radius-3)",
            padding: "var(--space-5)",
            cursor: locked ? "default" : "pointer",
            opacity: full ? 0.6 : 1,
            transition: "border-color 120ms, background-color 120ms",
          }}
        >
          <Flex direction="column" align="center" gap="2">
            <Upload size={22} color="var(--gray-9)" />
            <Text size="3" weight="medium">
              {full
                ? "No storage left"
                : busy
                  ? "Uploading…"
                  : dragging
                    ? "Drop to upload"
                    : title}
            </Text>
            <Text size="1" color="gray" align="center">
              {full
                ? "Delete something, or ask an admin to raise the club's quota."
                : hint}
            </Text>
            <Button
              variant="soft"
              size="2"
              disabled={locked}
              onClick={(e) => {
                // The box already opens the picker; without this the click
                // bubbles and opens it a second time.
                e.stopPropagation();
                inputRef.current?.click();
              }}
            >
              Browse files
            </Button>
          </Flex>
        </Box>

        {refused && (
          <Callout.Root color="red" size="1">
            <Callout.Icon>
              <AlertCircle size={14} />
            </Callout.Icon>
            <Callout.Text>{refused}</Callout.Text>
          </Callout.Root>
        )}

        <input
          ref={inputRef}
          type="file"
          accept={accept}
          multiple
          hidden
          onChange={(e) => e.target.files && void handleFiles(e.target.files)}
        />

        {items.length > 0 && (
          <Flex direction="column" gap="2">
            {busy && (
              <Flex direction="column" gap="1">
                <Flex justify="between">
                  <Text size="2" weight="medium">
                    {done} of {items.length} uploaded
                  </Text>
                  {failed.length > 0 && (
                    <Text size="1" color="red">
                      {failed.length} failed
                    </Text>
                  )}
                </Flex>
                <Progress value={(done / items.length) * 100} size="2" />
              </Flex>
            )}

            {failed.length > 0 && (
              <Callout.Root color="red" size="1">
                <Callout.Icon>
                  <AlertCircle size={14} />
                </Callout.Icon>
                <Callout.Text>
                  Failed to upload: {failed.map((f) => f.name).join(", ")}
                </Callout.Text>
              </Callout.Root>
            )}

            {listed.map((item) => (
              <Box key={item.name}>
                <Flex justify="between" gap="2">
                  <Text size="1" color="gray" truncate>
                    {item.name}
                  </Text>
                  <Text size="1" color="gray">
                    {item.status === "uploading" ? `${item.percent}%` : "waiting"}
                  </Text>
                </Flex>
                {/* Only the ones actually moving get a bar. A queued file has
                    nothing to report and a finished one has left the list. */}
                {item.status === "uploading" && <Progress value={item.percent} size="1" />}
              </Box>
            ))}

            {waiting > 0 && (
              <Text size="1" color="gray">
                {waiting} more waiting
              </Text>
            )}
          </Flex>
        )}
      </Flex>
    </Card>
  );
}
